import { getApiGalaxyServer, postApiGalaxyServer } from 'services';

/**
 * Galaxy's scheduled-maintenance window, served through GrowthBook.
 *
 * One `force` rule per team on the `scheduledMaintenanceFlag`, scoped by a condition on
 * (tenantId, realmId, teamId). Two consequences the UI has to respect:
 *
 * - Times are epoch **seconds**, not milliseconds. Galaxy rejects millisecond values
 *   outright rather than scheduling a window 50,000 years out.
 * - The scope is validated against the team's CURRENT realm. Before a migration the team
 *   is still indexed under the source realm, so a window must be set with the SOURCE
 *   coordinates or galaxy refuses it.
 */
export interface ScheduledMaintenanceWindow {
  ruleId?: string;
  type: string;
  managedByGalaxy: boolean;
  enabled: boolean;
  description?: string;
  condition?: string;
  value?: string;
  startTime: number | null;
  endTime: number | null;
  isActive: boolean;
  /** GrowthBook's own time gate. Galaxy never writes these, so `isActive` is unreliable here. */
  hasScheduleRules: boolean;
}

export interface ScheduledMaintenanceState {
  flag: string;
  environment: string;
  enabled: boolean;
  /** Served to every team matching no rule — an active default is environment-wide. */
  default: { value?: string; startTime: number | null; endTime: number | null; isActive: boolean };
  windows: ScheduledMaintenanceWindow[];
}

export interface SetScheduledMaintenancePayload {
  tenant: string;
  realmId: string;
  teamIds: (string | number)[];
  /** Epoch SECONDS. */
  startTime: number;
  /** Epoch SECONDS. Must be after startTime and in the future. */
  endTime: number;
}

export interface ClearScheduledMaintenancePayload {
  tenant: string;
  realmId: string;
  teamIds: (string | number)[];
}

export interface ClearScheduledMaintenanceResult {
  flag: string;
  environment: string;
  cleared: { teamId: string; ruleId: string }[];
  /** Teams that had no galaxy-managed rule — clearing is idempotent. */
  notFound: string[];
}

export interface SetScheduledMaintenanceResult {
  flag: string;
  environment: string;
  window: { startTime: number; endTime: number };
  applied: { teamId: string; ruleId: string; action: 'created' | 'updated' }[];
}

export const getScheduledMaintenance = ({ token, config = {} }: BaseApiParams<void>) =>
  getApiGalaxyServer<ScheduledMaintenanceState>('/growthbook/scheduledMaintenance', config, token);

export const setScheduledMaintenance = ({
  params: data,
  token,
  config = {},
}: BaseApiParams<SetScheduledMaintenancePayload>) =>
  postApiGalaxyServer<SetScheduledMaintenancePayload, SetScheduledMaintenanceResult>(
    '/growthbook/scheduledMaintenance/set',
    data,
    config,
    token,
  );

export const clearScheduledMaintenance = ({
  params: data,
  token,
  config = {},
}: BaseApiParams<ClearScheduledMaintenancePayload>) =>
  postApiGalaxyServer<ClearScheduledMaintenancePayload, ClearScheduledMaintenanceResult>(
    '/growthbook/scheduledMaintenance/clear',
    data,
    config,
    token,
  );
